import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BedDouble, Car, Compass, Landmark, LayoutGrid, Leaf, MapPin, UtensilsCrossed, Waves } from 'lucide-react';
import { ISLANDS } from '../data/experiences';
import { useCatalog } from '../data/catalog';
import { useLang } from '../i18n';
import { Reveal } from './Reveal';
import { SectionHeader } from './SectionHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { id: 'Todos', icon: LayoutGrid },
  { id: 'Experiências', icon: Compass },
  { id: 'Alojamentos', icon: BedDouble },
  { id: 'Natureza', icon: Leaf },
  { id: 'Cultura', icon: Landmark },
  { id: 'Mar', icon: Waves },
  { id: 'Gastronomia', icon: UtensilsCrossed },
  { id: 'Transfers', icon: Car },
];

const VIEWS = {
  'Todos': { center: [1, 22], zoom: 3 },
  'São Tomé': { center: [0.25, 6.65], zoom: 9 },
  'Príncipe': { center: [1.64, 7.41], zoom: 10 },
  'Santiago': { center: [15.05, -23.62], zoom: 9 },
};

const STATUS_COLOR = { available: '#1e4a3c', partner_network: '#c9a961', coming_soon: '#8a8f8c' };

const destPin = (status) =>
  L.divIcon({
    className: '',
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${STATUS_COLOR[status]};border:3px solid #fff;box-shadow:0 3px 12px rgba(0,0,0,.4)"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });

const pin = (bg, ring) =>
  L.divIcon({
    className: '',
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${bg};border:3px solid ${ring};box-shadow:0 2px 10px rgba(0,0,0,.35)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

const MAP_CAT_LABEL = {
  Todos: 'filter.all',
  'Experiências': 'nav.experiences',
  'Alojamentos': 'nav.stays',
  Natureza: 'cat.nature',
  Cultura: 'cat.culture',
  Mar: 'cat.sea',
  Gastronomia: 'cat.gastro',
  Transfers: 'nav.transfers',
};

export const MapSection = () => {
  const { t, lang } = useLang();
  const { experiences, transfers, destinations } = useCatalog();
  const navigate = useNavigate();
  const [island, setIsland] = useState('Todos');
  const [cat, setCat] = useState('Todos');
  const [partners, setPartners] = useState([]);
  const el = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);

  useEffect(() => {
    fetch(`${API}/partners`)
      .then((r) => r.json())
      .then((d) => setPartners(Array.isArray(d) ? d : []))
      .catch(() => setPartners([]));
  }, []);

  const items = useMemo(() => {
    const inIsland = (i) => island === 'Todos' || i === island;
    const exps =
      cat === 'Todos' || (cat !== 'Alojamentos' && cat !== 'Transfers')
        ? experiences.filter((e) => inIsland(e.island) && (cat === 'Todos' || e.cats.includes(cat))).map((e) => ({ ...e, kind: 'exp' }))
        : [];
    const showPartners = cat === 'Todos' || cat === 'Alojamentos' || cat === 'Experiências';
    const pts = showPartners
      ? partners
          .filter((p) => inIsland(p.island))
          .filter((p) => (cat === 'Alojamentos' ? p.type === 'accommodation' : cat === 'Experiências' ? p.type !== 'accommodation' && p.type !== 'transport' : true))
          .map((p) => ({ ...p, kind: 'partner' }))
      : [];
    const trs =
      cat === 'Todos' || cat === 'Transfers'
        ? transfers.filter((tr) => inIsland(tr.island)).flatMap((tr) =>
            tr.points.map((pt) => ({ ...pt, id: `${tr.slug}-${pt.name}`, slug: tr.slug, island: tr.island, kind: 'transfer' }))
          )
        : [];
    return [...exps, ...pts, ...trs];
  }, [island, cat, partners, experiences, transfers]);

  useEffect(() => {
    if (map.current) return;
    const m = L.map(el.current, { scrollWheelZoom: false, zoomControl: true }).setView(VIEWS.Todos.center, VIEWS.Todos.zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    map.current = m;
    const resizeObserver = new ResizeObserver(() => m.invalidateSize());
    resizeObserver.observe(el.current);
    const handleResize = () => m.invalidateSize();
    window.addEventListener('resize', handleResize);
    requestAnimationFrame(() => m.invalidateSize());
    setTimeout(() => m.invalidateSize(), 300);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      m.remove();
      map.current = null;
    };
  }, []);

  const destLayer = useRef(null);

  useEffect(() => {
    if (!map.current) return;
    if (destLayer.current) destLayer.current.remove();
    const lg = L.layerGroup();
    destinations.forEach((d) => {
      L.marker(d.coords, { icon: destPin(d.status) })
        .bindPopup(`<strong>${d.name}</strong><br/><em>${d.status === 'available' ? t('dst.available') : d.status === 'partner_network' ? t('dst.partner') : t('dst.coming_soon')}</em><br/>${d.positioning}<br/><a href="/destinations/${d.slug}" style="color:#1e4a3c;font-weight:600">${t('nav.explore')} →</a>`)
        .addTo(lg);
    });
    lg.addTo(map.current);
    destLayer.current = lg;
  }, [lang, destinations, t]);

  useEffect(() => {
    if (!layer.current) return;
    layer.current.clearLayers();
    items.forEach((e) => {
      if (typeof e.lat !== 'number' || typeof e.lng !== 'number') return;
      const color = e.kind === 'partner' ? pin('#c9a961', '#1e4a3c') : e.kind === 'transfer' ? pin('#1e2a24', '#c9a961') : pin('#1e4a3c', '#c9a961');
      L.marker([e.lat, e.lng], { icon: color })
        .bindPopup(`<strong>${e.name}</strong><br/>${e.island}`)
        .addTo(layer.current);
    });
  }, [items]);

  useEffect(() => {
    const v = VIEWS[island];
    map.current?.flyTo(v.center, v.zoom, { duration: 1.2 });
  }, [island]);

  const open = (e) => {
    if (e.kind === 'exp') navigate(`/experiences/${e.slug}`);
    else if (e.kind === 'partner') navigate(`/partners/${e.id}`);
    else navigate('/transfers');
  };

  const countKey = cat === 'Todos' || cat === 'Alojamentos' || cat === 'Transfers' ? 'map.found.partners' : 'map.found.exp';

  return (
    <section data-testid="map-section" className="overflow-hidden bg-eco-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeader
          align="center"
          eyebrow={t('map.kicker')}
          title={t('map.title')}
          text={t('map.text')}
        />

        <Reveal delay={0.1} className="mt-10 flex flex-wrap justify-center gap-2">
          {ISLANDS.map((i) => (
            <button
              key={i}
              data-testid={`map-island-filter-${i.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setIsland(i)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${
                island === i ? 'bg-eco-green text-white' : 'bg-white text-eco-ink/70 hover:bg-eco-green/10'
              }`}
            >
              {i === 'Todos' ? t('filter.all') : i}
            </button>
          ))}
        </Reveal>

        <Reveal delay={0.15} className="mt-4 flex flex-wrap justify-center gap-2">
          {CATS.map((c) => (
            <button
              key={c.id}
              data-testid={`map-cat-filter-${c.id.toLowerCase()}`}
              onClick={() => setCat(c.id)}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors duration-300 ${
                cat === c.id
                  ? 'border-eco-gold bg-eco-gold text-eco-deep'
                  : 'border-eco-ink/15 bg-white text-eco-ink/60 hover:border-eco-gold hover:text-eco-gold'
              }`}
            >
              <c.icon size={13} /> {t(MAP_CAT_LABEL[c.id] || c.id)}
            </button>
          ))}
        </Reveal>

        <Reveal delay={0.2} className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div ref={el} data-testid="leaflet-map" className="z-0 h-[26rem] w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-eco-ink/10 lg:h-[34rem]" />
          <div className="flex h-[26rem] min-w-0 flex-col lg:h-[34rem]">
            <p data-testid="map-results-count" className="mb-3 text-sm font-medium text-eco-ink/60">
              {t(countKey, { n: items.length })}
            </p>
            <div className="no-scrollbar flex-1 space-y-2.5 overflow-y-auto pr-1">
              {items.map((e) => (
                <button
                  key={e.kind === 'exp' ? e.slug : e.id}
                  data-testid={`map-item-${e.kind === 'exp' ? e.slug : e.id}`}
                  onClick={() => open(e)}
                  className="group flex w-full items-center gap-3.5 rounded-2xl border border-transparent bg-white/80 p-3 text-left transition-all duration-300 hover:border-eco-gold/40 hover:bg-white"
                >
                  {e.kind === 'exp' ? (
                    <img src={e.img} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                  ) : (
                    <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-eco-green font-serif text-xl text-eco-gold">
                      {e.name.charAt(0)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold tracking-[0.2em] uppercase text-eco-gold">{e.island}</p>
                    <p className="truncate text-sm font-semibold text-eco-ink">{e.name}</p>
                    <p className="flex items-center gap-1 text-xs text-eco-ink/50">
                      <MapPin size={10} /> {e.kind === 'exp' ? e.category : e.kind === 'transfer' ? 'Transfer' : t(`type.${e.type}`)}
                    </p>
                  </div>
                </button>
              ))}
              {items.length === 0 && (
                <p data-testid="map-empty-state" className="rounded-2xl border border-dashed border-eco-ink/15 py-10 text-center text-sm text-eco-ink/50">
                  {t(countKey, { n: 0 })}
                </p>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
